import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useHistory, useParams } from 'react-router-dom';
import Loader from 'react-loader-spinner';
import { FiFileText, FiPlus, FiSave } from 'react-icons/fi';
import { API_URLS, getTournamentImageUrl } from '../constants/config';
import { useI18n } from '../utilities/i18n';
import { authorizedPost, fileToBase64, getApiErrorMessage } from '../utilities/stadiumsApi';
import { useChampionships } from '../utilities/championships';
import { useAdminAccess } from '../utilities/adminAccess';
import {
  curatorDisplayName,
  curatorInfoText,
  extractEntity,
  extractList,
  isEnabled,
  withCacheBust,
} from '../utilities/tournamentsApi';
import AdminPhotoUpload from '../components/AdminPhotoUpload';
import ReorderableTable from '../components/ReorderableTable';
import StadiumCrumbs from '../components/StadiumCrumbs';
import CustomModal from '../components/CustomModal';
import defaultAvatar from '../images/defaultAvatar.png';
import '../styles/createMatchModal.css';
import '../styles/stadiumsManagement.css';
import '../styles/disqualificationsManagement.css';
import '../styles/tournamentsManagement.css';

const emptyForm = {
  enabled: true,
  name: '',
  country_id: '',
};

function socialHref(value) {
  const text = String(value || '').trim();
  if (!text) return '';
  if (/^https?:\/\//i.test(text)) return text;
  if (text.startsWith('@')) return `https://t.me/${text.slice(1)}`;
  if (/^\+?\d[\d\s()-]{5,}$/.test(text)) return `tel:${text.replace(/\s+/g, '')}`;
  if (/t\.me\//i.test(text)) return text.startsWith('http') ? text : `https://${text}`;
  return text;
}

export default function ManageLeague() {
  const { countryId, leagueId } = useParams();
  const isNew = !leagueId;
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useI18n();
  const { champId, loading: champsLoading } = useChampionships();
  const { canWrite } = useAdminAccess('tournaments');

  const [form, setForm] = useState({ ...emptyForm, country_id: countryId || '' });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [photoError, setPhotoError] = useState('');
  const [errors, setErrors] = useState({});
  const [countries, setCountries] = useState([]);
  const [countryName, setCountryName] = useState('');
  const [seasons, setSeasons] = useState([]);
  const [originalSeasons, setOriginalSeasons] = useState([]);
  const [curators, setCurators] = useState([]);
  const [reorderMode, setReorderMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteVisible, setDeleteVisible] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  useEffect(() => {
    if (!champId && champsLoading) return;
    initPage();
  }, [countryId, leagueId, champId, champsLoading]);

  const initPage = async () => {
    setMessage({ text: '', type: '' });
    setErrors({});
    setPhotoFile(null);
    setPhotoError('');
    setReorderMode(false);

    if (!champId) {
      setLoading(false);
      setMessage({ text: t('championshipsLoadError'), type: 'error' });
      return;
    }

    setLoading(true);
    try {
      const countriesResponse = await authorizedPost(dispatch, API_URLS.getAdminCountries, { champ_id: champId });
      const countryOptions = extractList(countriesResponse.data);
      setCountries(countryOptions);
      const currentCountry = countryOptions.find((item) => Number(item.id) === Number(countryId));
      setCountryName(currentCountry?.name || '');

      if (isNew) {
        setForm({
          ...emptyForm,
          country_id: countryId || (countryOptions[0] && countryOptions[0].id) || '',
        });
        setPhotoPreview('');
        setSeasons([]);
        setOriginalSeasons([]);
        setCurators([]);
        return;
      }

      const response = await authorizedPost(dispatch, API_URLS.getAdminLeague, { id: Number(leagueId) });
      const league = extractEntity(response.data);
      setForm({
        enabled: isEnabled(league.enabled),
        name: league.name || '',
        country_id: league.country_id || countryId || '',
      });
      setPhotoPreview(league.image ? withCacheBust(league.image) : '');
      const nextSeasons = extractList(league.seasons);
      setSeasons(nextSeasons);
      setOriginalSeasons(nextSeasons);
      setCurators(extractList(league.curators));
      if (league.country_id) {
        const leagueCountry = countryOptions.find((item) => Number(item.id) === Number(league.country_id));
        if (leagueCountry) setCountryName(leagueCountry.name);
      }
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('stLoadError')), type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => ({ ...prev, [key]: '' }));
    }
  };

  const validate = () => {
    const nextErrors = {};
    if (!form.name.trim()) nextErrors.name = t('stdNameRequired');
    if (!form.country_id) nextErrors.country_id = t('trCountryRequired');
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    setMessage({ text: '', type: '' });
    try {
      const payload = {
        name: form.name.trim(),
        enabled: form.enabled ? 1 : 0,
        country_id: Number(form.country_id),
        champ_id: champId,
      };
      if (photoFile) {
        payload.image = await fileToBase64(photoFile);
      }

      if (isNew) {
        const response = await authorizedPost(dispatch, API_URLS.addLeague, payload);
        const created = extractEntity(response.data);
        setMessage({ text: t('trLeagueCreated'), type: 'success' });
        const nextCountryId = created.country_id || form.country_id;
        if (created.id) {
          history.replace(`/tournaments/${nextCountryId}/league/${created.id}`);
        } else {
          history.push(`/tournaments/${countryId}`);
        }
      } else {
        await authorizedPost(dispatch, API_URLS.changeLeague, {
          ...payload,
          id: Number(leagueId),
        });
        setPhotoFile(null);
        setMessage({ text: t('stSaved'), type: 'success' });
        if (String(form.country_id) !== String(countryId)) {
          history.replace(`/tournaments/${form.country_id}/league/${leagueId}`);
        }
      }
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('stSaveError')), type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await authorizedPost(dispatch, API_URLS.deleteLeague, { id: Number(leagueId) });
      history.push(`/tournaments/${countryId}`);
    } catch (error) {
      setDeleteVisible(false);
      setMessage({ text: getApiErrorMessage(error, t('stdDeleteError')), type: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const handleSaveSeasonOrder = async () => {
    setSavingOrder(true);
    setMessage({ text: '', type: '' });
    try {
      await authorizedPost(dispatch, API_URLS.changeSeasonsPriority, {
        seasons: seasons.map((item, index) => ({ id: item.id, sortIdx: index })),
      });
      const ordered = seasons.map((item, index) => ({ ...item, sortIdx: index }));
      setSeasons(ordered);
      setOriginalSeasons(ordered);
      setReorderMode(false);
      setMessage({ text: t('stOrderSaved'), type: 'success' });
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('stOrderError')), type: 'error' });
    } finally {
      setSavingOrder(false);
    }
  };

  const seasonColumns = [
    {
      key: 'name',
      label: t('stdName'),
      className: 'st-cell-name',
      render: (item, index) => (
        <span className="tr-name tr-season-name">
          <span className="tr-season-title">{item.name || '—'}</span>
          {index === 0 ? (
            <span className="st-badge enabled">
              <span className="tr-btn-full">{t('trActive')}</span>
              <span className="tr-btn-short">{t('trActiveShort')}</span>
            </span>
          ) : null}
        </span>
      ),
    },
    {
      key: 'sortIdx',
      label: t('stdPriority'),
      className: 'st-cell-priority',
      render: (item, index) => (
        <span className="st-priority-badge">{reorderMode ? index + 1 : (item.sortIdx ?? index + 1)}</span>
      ),
    },
  ];

  const curatorColumns = [
    {
      key: 'name',
      label: t('stdName'),
      className: 'st-cell-name',
      render: (item) => (
        <span className="tr-contact">
          <img
            src={item.image ? getTournamentImageUrl(item.image) : defaultAvatar}
            alt=""
            className="tr-contact-photo"
            onError={(event) => {
              event.currentTarget.src = defaultAvatar;
            }}
          />
          <span>{curatorDisplayName(item) || '—'}</span>
        </span>
      ),
    },
    {
      key: 'info',
      label: t('trInfo'),
      className: 'st-cell-info st-cell-muted',
      render: (item) => {
        const info = curatorInfoText(item);
        return (
          <div className="tr-contact-info">
            {info.position ? <span className="tr-contact-position">{info.position}</span> : null}
            {info.links.length ? (
              <span className="tr-contact-links">
                {info.links.map((link) => (
                  <a
                    key={link}
                    href={socialHref(link)}
                    onClick={(event) => event.stopPropagation()}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {link}
                  </a>
                ))}
              </span>
            ) : null}
            {!info.position && !info.links.length ? '—' : null}
          </div>
        );
      },
    },
  ];

  const crumbs = [
    { label: t('navTournaments'), to: '/tournaments' },
    { label: countryName || t('trCountry'), to: `/tournaments/${countryId}` },
    { label: isNew ? t('trNewLeague') : (form.name.trim() || t('trEditLeague')) },
  ];

  const leagueForm = (
    <div className="tr-league-form">
      <div className="st-field">
        <label className="st-check">
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(event) => updateField('enabled', event.target.checked)}
          />
          {t('stShowOnSite')}
        </label>
      </div>

      <div className="tr-curator-main">
        <div className="st-field tr-curator-photo">
          <label>{t('stPhoto')}</label>
          <AdminPhotoUpload
            preview={photoPreview}
            error={photoError}
            disabled={saving}
            enableCrop={false}
            onFileChange={(file, error, preview) => {
              if (error) {
                setPhotoError(error);
                return;
              }
              setPhotoError('');
              setPhotoFile(file);
              setPhotoPreview(preview);
            }}
          />
        </div>

        <div className="tr-league-fields">
          <div className="st-field">
            <label>{t('stdName')} <span className="st-required">*</span></label>
            <input
              className={`st-input ${errors.name ? 'error' : ''}`}
              value={form.name}
              placeholder={t('trLeagueNamePh')}
              onChange={(event) => updateField('name', event.target.value)}
            />
            {errors.name ? <span className="st-field-error">{errors.name}</span> : null}
          </div>

          <div className="st-field">
            <label>{t('trCountry')} <span className="st-required">*</span></label>
            <select
              className={`st-select ${errors.country_id ? 'error' : ''}`}
              value={form.country_id}
              onChange={(event) => updateField('country_id', event.target.value)}
            >
              <option value="">{t('trCountryPlaceholder')}</option>
              {countries.map((item) => (
                <option key={item.id} value={item.id}>{item.name}</option>
              ))}
            </select>
            {errors.country_id ? <span className="st-field-error">{errors.country_id}</span> : null}
          </div>
        </div>
      </div>
    </div>
  );

  const seasonActions = reorderMode ? (
    <>
      <button
        type="button"
        className="st-btn st-btn-secondary"
        disabled={savingOrder}
        onClick={() => {
          setSeasons(originalSeasons);
          setReorderMode(false);
        }}
      >
        {t('stCancelReorder')}
      </button>
      <button
        type="button"
        className="st-btn st-btn-primary"
        disabled={savingOrder || !seasons.length}
        onClick={handleSaveSeasonOrder}
      >
        <FiSave /> {t('stSaveOrder')}
      </button>
    </>
  ) : (
    <>
      <button
        type="button"
        className="st-btn st-btn-secondary"
        disabled={loading || seasons.length < 2}
        onClick={() => {
          setOriginalSeasons(seasons);
          setReorderMode(true);
        }}
      >
        <span className="tr-btn-full">{t('stReorder')}</span>
        <span className="tr-btn-short">{t('stReorderShort')}</span>
      </button>
      <Link
        to={`/tournaments/${countryId}/league/${leagueId}/season/new`}
        className={`st-btn st-btn-primary${loading ? ' is-disabled' : ''}`}
        aria-disabled={loading || undefined}
        onClick={(event) => { if (loading) event.preventDefault(); }}
      >
        <FiPlus />
        <span className="tr-btn-full">{t('trAddSeason')}</span>
        <span className="tr-btn-short">{t('trAddSeasonShort')}</span>
      </Link>
    </>
  );

  if (isNew) {
    return (
      <div className="layoutWrapper st-layout">
        <div className="matchCenterWrapper st-page">
          <div className="matchCenterHeader">
            <div className="st-header">
              <StadiumCrumbs items={crumbs} />
            </div>
          </div>

          <div className="st-scroll">
            <div className="st-content">
              {message.text ? <div className={`st-message ${message.type}`}>{message.text}</div> : null}
              <div className="st-block">
                {leagueForm}
              </div>
            </div>
          </div>

          <div className="st-actions">
            {canWrite ? (
            <button
              type="button"
              className="st-btn st-btn-primary"
              disabled={saving}
              onClick={handleSave}
            >
              <FiSave /> {saving ? t('trLoading') : t('trCreateLeague')}
            </button>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="layoutWrapper st-layout">
      <div className="matchCenterWrapper st-page dq-page tr-league-edit">
        <div className="matchCenterHeader">
          <div className="st-header">
            <StadiumCrumbs items={crumbs} />
          </div>
        </div>

        {message.text ? <div className={`st-message ${message.type}`}>{message.text}</div> : null}

        <div className="st-scroll dq-scroll">
          <div className="dq-split">
            <section className="st-block dq-panel">
              <h2 className="st-block-header">{t('trSeasons')}</h2>
              <p className="st-hint">{t('trActiveSeasonHint')}</p>
              <div className="dq-panel-body">
                {loading ? (
                  <div className="st-loading">
                    <Loader type="Oval" color="#3ad1a0" height={48} width={48} />
                    <p>{t('trLoading')}</p>
                  </div>
                ) : (
                  <ReorderableTable
                    tableClassName="st-table-two st-table-tr-seasons"
                    columns={seasonColumns}
                    items={seasons}
                    getRowKey={(item) => item.id}
                    reorderMode={reorderMode}
                    onItemsChange={setSeasons}
                    getRowHref={(item) => (item.id ? `/tournaments/${countryId}/league/${leagueId}/season/${item.id}` : null)}
                    empty={<div className="st-empty"><p>{t('trEmptySeasons')}</p></div>}
                  />
                )}
                {reorderMode ? <p className="st-hint">{t('stDragHint')}</p> : null}
              </div>
              <div className="st-actions">
                {canWrite ? seasonActions : null}
              </div>
            </section>

            <div className="dq-stack">
              <section className="st-block dq-panel">
                <h2 className="st-block-header">{t('trEditLeague')}</h2>
                <div className="dq-panel-body">
                  {loading ? (
                    <div className="st-loading">
                      <Loader type="Oval" color="#3ad1a0" height={48} width={48} />
                      <p>{t('trLoading')}</p>
                    </div>
                  ) : leagueForm}
                </div>
                <div className="st-actions">
                  {canWrite ? (
                  <button
                    type="button"
                    className="st-btn st-btn-danger"
                    disabled={loading || saving || deleting}
                    onClick={() => setDeleteVisible(true)}
                  >
                    {t('delete')}
                  </button>
                  ) : null}
                  <Link
                    to={`/tournaments/${countryId}/league/${leagueId}/rules`}
                    className={`st-btn st-btn-secondary${loading ? ' is-disabled' : ''}`}
                    aria-disabled={loading || undefined}
                    onClick={(event) => { if (loading) event.preventDefault(); }}
                  >
                    <FiFileText />
                    <span className="tr-btn-full">{t('trRulesOpen')}</span>
                    <span className="tr-btn-short">{t('trRules')}</span>
                  </Link>
                  {canWrite ? (
                  <button
                    type="button"
                    className="st-btn st-btn-primary"
                    disabled={loading || saving}
                    onClick={handleSave}
                  >
                    <FiSave />
                    {saving ? t('trLoading') : (
                      <>
                        <span className="tr-btn-full">{t('stdSaveChanges')}</span>
                        <span className="tr-btn-short">{t('stdSaveChangesShort')}</span>
                      </>
                    )}
                  </button>
                  ) : null}
                </div>
              </section>

              <section className="st-block dq-panel">
                <h2 className="st-block-header">{t('trContacts')}</h2>
                <div className="dq-panel-body">
                  {loading ? (
                    <div className="st-loading">
                      <Loader type="Oval" color="#3ad1a0" height={48} width={48} />
                      <p>{t('trLoading')}</p>
                    </div>
                  ) : (
                    <ReorderableTable
                      tableClassName="st-table-contacts"
                      columns={curatorColumns}
                      items={curators}
                      getRowKey={(item) => item.id}
                      reorderMode={false}
                      getRowHref={(item) => (item.id ? `/tournaments/${countryId}/league/${leagueId}/curator/${item.id}` : null)}
                      empty={<div className="st-empty"><p>{t('trEmptyContacts')}</p></div>}
                    />
                  )}
                </div>
                <div className="st-actions">
                  {canWrite ? (
                  <Link
                    to={`/tournaments/${countryId}/league/${leagueId}/curator/new`}
                    className={`st-btn st-btn-primary${loading ? ' is-disabled' : ''}`}
                    aria-disabled={loading || undefined}
                    onClick={(event) => { if (loading) event.preventDefault(); }}
                  >
                    <FiPlus /> {t('trAddContact')}
                  </Link>
                  ) : null}
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>

      <CustomModal
        styles={{ width: 420, height: 'auto', paddingLeft: 32, paddingRight: 32 }}
        visible={deleteVisible}
        closeModal={() => setDeleteVisible(false)}
      >
        <div className="st-delete-modal">
          <div className="customModalHeader">{t('trDeleteLeagueTitle')}</div>
          <p>{t('trDeleteLeagueText')}</p>
          <div className="st-delete-actions">
            <button type="button" className="btn-modal btn-secondary-modal" onClick={() => setDeleteVisible(false)}>
              {t('cancel')}
            </button>
            <button type="button" className="btn-modal btn-danger-modal" disabled={deleting} onClick={handleDelete}>
              {t('delete')}
            </button>
          </div>
        </div>
      </CustomModal>
    </div>
  );
}
